import { Injectable } from "@angular/core";
import { NgrxHttpClient } from "@library/utils/services/http-service";
import { BehaviorSubject, catchError, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, take, withLatestFrom } from "rxjs";
import { GraphType } from "./control-page-store.service";
import { isDefined } from "@library/utils/helpers/store/is-defined";
import { HomesFacade } from "@library/store/homes/homes.facade";
import { SUPPORTED_CONTROL_ENERGY_MODULES, SUPPORTED_CONTROL_GATEWAYS, SUPPORTED_CONTROL_WEATHER_MODULES } from "./control-page.const";
import { ModulesFacade } from "@library/store/modules/modules.facade";
import { Router } from "@angular/router";

@Injectable()
export class ControlPageService {

  private readonly daySelected = new BehaviorSubject<number>(0);
  readonly daySelected$ = this.daySelected.asObservable();

  hasHome$ = this.homesFacade.homesSuccess$.pipe(
    switchMap(() => this.homesFacade.homes$),
    map(homes => homes.length > 0),
    startWith(true),
    distinctUntilChanged(),
  );

  hasModules$ = this.homesFacade.currentHomeId$.pipe(
    isDefined(),
    switchMap(() => this.modulesFacade.currentHomeModules$),
    map(modules => modules.some(m => SUPPORTED_CONTROL_GATEWAYS.includes(m.type))),
    shareReplay(1),
    startWith(true),
    distinctUntilChanged(),
  );

  hasUnplacedProducts$ = this.homesFacade.currentHomeId$.pipe(
    isDefined(),
    withLatestFrom(this.modulesFacade.currentHomeModules$, this.homesFacade.currentHome$),
    map(([, modules, home]) => modules.filter(m => SUPPORTED_CONTROL_WEATHER_MODULES.includes(m.type)).some(m => !m.room_id) && !home.mode),
    distinctUntilChanged(),
    startWith(false),
  );

  hasThermostatOrValve$ = this.homesFacade.currentHomeId$.pipe(
    isDefined(),
    withLatestFrom(this.modulesFacade.currentHomeModules$),
    map(([, modules]) => modules.some(m => SUPPORTED_CONTROL_ENERGY_MODULES.includes(m.type))),
    distinctUntilChanged(),
    startWith(null),
  );
  constructor(
    public http: NgrxHttpClient,
    private homesFacade: HomesFacade,
    private modulesFacade: ModulesFacade,
    private router: Router,
  ) {}

  setForecastDaySelected(index: number): void {
    this.daySelected.next(index);
  }

  getForecastData(params: {device_id: string, module_id: string, locale: string, ask_graphs: string[]}){
    return this.http.post('simplifiedfuturemeasure', params).pipe(
      map(m => m.body),
      take(1),
      catchError((error) => of(error)),
    );
  }

  public isTemperatureModule(graphType: GraphType){
    if(graphType === null){
      return false;
    }
    return graphType === GraphType.THERM || graphType.toString() === GraphType.TEMPERATURE.toString();
  }

  controlPageGetRoute(hasHome: boolean, hasModules: boolean, hasUnplacedProducts: boolean, hasThermostatOrValve: boolean) {

    let route = null;

    // if we are already on no module page go back to dashboard
    if (this.router.url.includes('/control/no-module') || this.router.url.includes('/control/assistant') ) {
      route = '/control/dashboard';
    }

    if ((hasThermostatOrValve !== null && !hasThermostatOrValve) && this.router.url.includes('/control/reports')) {
      route = '/control/dashboard';
    }

    if (hasUnplacedProducts) {
      route = '/control/assistant';
    }

    if (!hasModules) {
      route = '/control/no-module';
    }

    if (!hasHome) {
      route = '/control/no-home';
    }

    if (route) {
      this.router.navigate([route]);
    }
  }
}
